import React, { useState, useMemo } from 'react';
import { IndianRupee, FileText } from 'lucide-react';

const Reports = ({ records, numberLinks = [] }) => {
  const today = new Date().toLocaleDateString('en-CA');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [filterType, setFilterType] = useState('all');

  const allRecords = [
    ...records.map(r => ({ ...r, category: 'PUC' })),
    ...numberLinks.map(r => ({ ...r, category: 'Number Link' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Filter records based on selected date range and type/payment
  const filteredRecords = allRecords.filter(record => {
    const recordDate = new Date(record.record_date).toLocaleDateString('en-CA');
    const matchesDate = recordDate >= startDate && recordDate <= endDate;
    if (!matchesDate) return false;

    if (filterType === 'cash') {
      return record.payment_method === 'cash' || !record.payment_method;
    } else if (filterType === 'paytm') {
      return record.payment_method === 'paytm';
    } else if (filterType === 'number-link') {
      return record.category === 'Number Link' && record.type !== 'PUC Reprint';
    } else if (filterType === 'puc-reprint') {
      return record.category === 'Number Link' && record.type === 'PUC Reprint';
    } else if (filterType === 'puc') {
      return record.category === 'PUC';
    }
    return true;
  });

  const totalCollection = filteredRecords.reduce((acc, record) => {
    return acc + Number(record.amount);
  }, 0);

  // Group collections by date for day-wise summary
  const dayWiseData = useMemo(() => {
    const dayMap = {};
    filteredRecords.forEach(record => {
      const dateStr = new Date(record.record_date).toLocaleDateString('en-GB');
      if (!dayMap[dateStr]) {
        dayMap[dateStr] = { 
          date: dateStr, 
          amount: 0, 
          count: 0, 
          sortKey: new Date(record.record_date).getTime() 
        };
      }
      dayMap[dateStr].amount += Number(record.amount);
      dayMap[dateStr].count += 1;
    });

    return Object.values(dayMap).sort((a, b) => b.sortKey - a.sortKey);
  }, [filteredRecords]);

  return (
    <div>
      {/* Date & Type Filters */}
      <div className="dash-card">
        <h4 className="form-section-title">Filter Reports</h4>
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 0 }}>
          <div className="form-group">
            <label htmlFor="startDate">Start Date</label>
            <input 
              type="date" 
              id="startDate" 
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="endDate">End Date</label>
            <input 
              type="date" 
              id="endDate" 
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="filterType">Filter By Type / Payment</label>
            <select
              id="filterType"
              className="form-control"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Transactions</option>
              <option value="cash">Cash Only</option>
              <option value="paytm">Paytm / Online</option>
              <option value="puc">PUC Entries Only</option>
              <option value="number-link">Number Links Only</option>
              <option value="puc-reprint">PUC Reprints Only</option>
              <option value="day-wise">Day-wise Summary Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="form-grid" style={{ gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="dash-card" style={{ marginBottom: 0 }}>
          <div className="metric-widget">
            <div className="metric-icon">
              <IndianRupee size={32} />
            </div>
            <div>
              <div className="metric-value">₹{totalCollection.toFixed(2)}</div>
              <div className="metric-label">Total Collection</div>
            </div>
          </div>
        </div>

        <div className="dash-card" style={{ marginBottom: 0 }}>
          <div className="metric-widget">
            <div className="metric-icon" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))', color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.3)' }}>
              <FileText size={32} />
            </div>
            <div>
              <div className="metric-value">{filteredRecords.length}</div>
              <div className="metric-label">Total Entries</div>
            </div>
          </div>
        </div>
      </div>

      {/* Conditionally show Day-wise Summary OR Details Table */}
      {filterType === 'day-wise' ? (
        <div className="dash-card">
          <div className="card-header">
            <h3>Day-wise Summary</h3>
          </div>
          <div className="table-responsive" style={{ border: 'none', background: 'transparent' }}>
            <table className="mobile-cards">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Entries</th>
                  <th>Total Collection</th>
                </tr>
              </thead>
              <tbody>
                {dayWiseData.length > 0 ? (
                  dayWiseData.map(day => (
                    <tr key={day.date}>
                      <td data-label="Date" style={{ fontWeight: 600 }}>{day.date}</td>
                      <td data-label="Entries">{day.count} {day.count === 1 ? 'entry' : 'entries'}</td>
                      <td data-label="Total Collection" style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1.1rem' }}>
                        ₹{day.amount.toFixed(2)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      No records found in this date range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="dash-card">
          <div className="card-header">
            <h3>Report Details</h3>
          </div>
          <div className="table-responsive" style={{ border: 'none', background: 'transparent' }}>
            <table className="mobile-cards">
              <thead>
                <tr>
                  <th>Record Info</th>
                  <th>Customer</th>
                  <th>Vehicle Details</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length > 0 ? (
                  filteredRecords.map(record => (
                    <tr key={`${record.category}-${record.id}`}>
                      <td data-label="Record Info">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <span className={`badge ${record.category === 'PUC' ? 'badge-diesel' : (record.type === 'PUC Reprint' ? 'badge-cng' : 'badge-petrol')}`} style={{ width: 'fit-content' }}>
                            {record.category === 'PUC' ? 'PUC' : (record.type || 'Number Link')}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            {new Date(record.record_date).toLocaleDateString('en-GB')}
                          </span>
                        </div>
                      </td>
                      <td data-label="Customer">
                        <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{record.category === 'PUC' ? (record.customer_name || '-') : record.person_name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{record.mobile_number || 'No Number'}</div>
                      </td>
                      <td data-label="Vehicle Details">
                        <div style={{ fontWeight: 700, letterSpacing: '0.5px' }}>{record.vehicle_number ? record.vehicle_number.toUpperCase() : '-'}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {record.vehicle_type && <span>{record.vehicle_type}</span>}
                          {record.fuel_type && <span>• {record.fuel_type}</span>}
                          {record.puc_expire_tenure && <span>• {record.puc_expire_tenure}M</span>}
                        </div>
                      </td>
                      <td data-label="Amount" style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1.1rem' }}>
                        ₹{Number(record.amount).toFixed(2)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      No records found in this date range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
